import React from 'react'
import { pipe, reduce } from 'ramda'
import { formatDateUTC } from 'sidecar-utility-library'

import enhance from './enhance'
import {
  Acknowledgement,
  ChangeNotification,
  MisstatementActionButton,
  MisstatementButtonContainer,
  MisstatementButtons,
  Text,
  Title,
  SubTitle,
  SubLine,
  SubTitleStep,
  SubLineStep,
  LoadingSpinner,
} from './styles'
import ChangeDelta from './ChangeDelta'

const Component = props => {
  const {
    // General props
    type = 'warn',
    format,
    hasPolicyPendingChanges,
    renderPolicyPendingChanges,
    renderActiveMisstatement,
    // Pending/current values
    cost = {},
    monthlyCost = {},
    allowance = {},
    deductible = {},
    prescriptionsCovered = {},
    prescriptionsTaken = {},
    procedures = {},
    denyReason = {},
    voluntaryExclusion = {},
    maternityCovered = {},
    maternityAllowance = {},
    maternityDeductible = {},
    threeYearRateLock = {},

    //Misstatement
    misstatementData = {},
    onClickMisstatementDecisionButtons,
    misstatementBusy,

    // pendingAcceptance
    isPendingAcceptance = false,
    acknowledgement,

    // policy canceled or expired
    isCanceledOrExpired = false,
  } = props

  const {
    misstatementReason = '',
    misstatementAttrChanges = [],
    cancellationDate,
    acceptByDate,
    missedPremium,
  } = misstatementData

  const uniqueMisstatementChanges = pipe(
    reduce(
      (acc, el = {}) => ({
        ...acc,
        [el.attributeName]: el,
      }),
      {},
    ),
    Object.values,
  )(misstatementAttrChanges)

  return (
    <ChangeNotification
      format={format}
      note={{
        type,
        text: (
          <div>
            <div>
              {hasPolicyPendingChanges && renderPolicyPendingChanges && (
                <div>
                  <Title>Pending changes</Title>
                  <ul>
                    <ChangeDelta
                      name="cost"
                      label="Recurring payments:"
                      benefit={cost.pending ? cost : monthlyCost}
                    />
                    <ChangeDelta
                      name="allowance"
                      label="Annual coverage:"
                      benefit={allowance}
                    />
                    <ChangeDelta
                      name="deductible"
                      label="Deductible:"
                      benefit={deductible}
                    />
                    <ChangeDelta
                      name="prescriptionsCovered"
                      label="Prescriptions:"
                      benefit={prescriptionsCovered}
                    />
                    <ChangeDelta
                      name="prescriptionsTaken"
                      label="Prescriptions taken:"
                      benefit={prescriptionsTaken}
                    />
                    <ChangeDelta
                      name="maternityCovered"
                      label="Maternity:"
                      benefit={maternityCovered}
                    />
                    <ChangeDelta
                      name="maternityDeductible"
                      label="Maternity deductible:"
                      benefit={maternityDeductible}
                    />
                    <ChangeDelta
                      name="maternityAllowance"
                      label="Maternity annual coverage:"
                      benefit={maternityAllowance}
                    />

                    <ChangeDelta
                      name="threeYearRateLock"
                      label="Three year rate lock:"
                      benefit={threeYearRateLock}
                    />

                    <ChangeDelta
                      name="procedures"
                      label="Upcoming procedure:"
                      benefit={procedures}
                    />
                    <ChangeDelta
                      name="denyReason"
                      label="Deny reason:"
                      benefit={denyReason}
                    />

                    <ChangeDelta
                      name="voluntaryExclusion"
                      label="Voluntary exclusion:"
                      benefit={voluntaryExclusion}
                    />
                  </ul>

                  {isPendingAcceptance && (
                    <Acknowledgement
                      format={format}
                      data-testid="info_acknowledgement"
                    >
                      {acknowledgement}
                    </Acknowledgement>
                  )}
                </div>
              )}
            </div>
            <div>
              {renderActiveMisstatement && (
                <div>
                  <Title>
                    {isCanceledOrExpired
                      ? 'Misstatement'
                      : 'Pending misstatement'}
                    {misstatementBusy && <LoadingSpinner />}
                  </Title>
                  <Text format={format} data-testid="info_misstatement_Text">
                    {`${misstatementReason}.`}
                  </Text>

                  <SubTitle data-testid="info_misstatement_subtitle">
                    Policy should look like
                  </SubTitle>
                  <ul>
                    {uniqueMisstatementChanges.map((change, idx) => {
                      const formattedNameFromUnderscore = change.attributeName
                        .toLowerCase()
                        .split('_')
                        .join(' ')
                      const formattedMedicalAnswers = value =>
                        value.split(',').join('\n')
                      return (
                        <ChangeDelta
                          key={`misstatement-${change.attributeName}-${idx}`}
                          name={change.attributeName}
                          label={formattedNameFromUnderscore}
                          benefit={{
                            current: change.oldValue
                              ? change.attributeName === 'MEDICAL_ANSWERS'
                                ? formattedMedicalAnswers(change.oldValue)
                                : change.oldValue
                              : 'N/A',
                            pending:
                              change.attributeName === 'MEDICAL_ANSWERS'
                                ? formattedMedicalAnswers(change.newValue)
                                : change.newValue,
                          }}
                        />
                      )
                    })}
                  </ul>
                  <div>
                    <SubTitle data-testid="info_misstatement_missed_premium_title">
                      Missed Premiums
                    </SubTitle>
                    <SubLine data-testid="info_misstatement_missed_premium">
                      {missedPremium}
                    </SubLine>
                  </div>

                  <div>
                    <SubTitleStep data-testid="info_misstatement_next_step">
                      Next steps
                    </SubTitleStep>
                    <ul>
                      <li>
                        <SubLineStep data-testid="info_misstatement_step_accept">
                          {`They can accept the following changes by ${formatDateUTC(
                            'MMM DD, YYYY',
                            acceptByDate,
                          )}.`}
                        </SubLineStep>
                      </li>
                      <li>
                        <SubLineStep data-testid="info_misstatement_step_deny">
                          They can deny and have the policy be rescinded.
                        </SubLineStep>
                      </li>
                      <li>
                        <SubLineStep data-testid="info_misstatement_step_cancel">
                          {`They can do nothing and be cancelled as of ${formatDateUTC(
                            'MMM DD, YYYY',
                            cancellationDate,
                          )}.`}
                        </SubLineStep>
                      </li>
                    </ul>
                  </div>
                  {!isCanceledOrExpired && (
                    <MisstatementButtons>
                      <MisstatementButtonContainer>
                        <MisstatementActionButton
                          theme="alt"
                          icon="x"
                          iconPosition="before"
                          data-testid="btn_cancel"
                          onClick={onClickMisstatementDecisionButtons('cancel')}
                          disabled={misstatementBusy}
                        >
                          Cancel
                        </MisstatementActionButton>
                      </MisstatementButtonContainer>
                    </MisstatementButtons>
                  )}
                </div>
              )}
            </div>
          </div>
        ),
      }}
    />
  )
}

Component.displayName = 'PolicyPendingChange'

export default enhance(Component)
